import { Component } from '@angular/core';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { EulaService } from '@lsq-frontend/fasttrack-shared/features/eula-service';
import { ResponsiveService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { map } from 'rxjs/operators';

@Component({
    selector: 'eula-age',
    styleUrls: ['./eula-page.component.scss'],
    template: `
        <div class="page-container container-fluid">
            <div class="eula-header">
                <h1>
                    {{ pageHeader }}
                </h1>
            </div>
            <lsq-legal-document
                [documentName]="eulaName"
                [content]="eulaContent"
                (scrolledToBottom)="reachedBottom = true"
            ></lsq-legal-document>
            <div class="eula-controls">
                <lsq-checkbox
                    [(ngModel)]="acceptedTerms"
                    [style.marginTop]="'2rem'"
                    [label]="checkboxLabel"
                    [disabled]="!reachedBottom"
                ></lsq-checkbox>
                <lsq-button
                    [disabled]="!acceptedTerms"
                    [style.margin]="'3rem auto'"
                    [size]="buttonSize$ | async"
                    [submitting]="isAccepting"
                    (buttonClick)="acceptEula()"
                >
                    Accept and Continue
                </lsq-button>
            </div>
        </div>
        <lsq-footer [isGray]="true"></lsq-footer>
    `
})
export class EulaPageComponent {
    public pageHeader = this._i18n('Please read and accept our End-User License Agreement');
    public eulaName = this._i18n('End-User License Agreement');

    public checkboxLabel = this._i18n(
        'I have scrolled through and read, and I accept the terms in the End User License Agreement.'
    );

    public acceptedTerms = false;
    public reachedBottom = false;

    public eulaContent = `
            <h3>LAST UPDATED: APRIL 20, 2021</h3>
            <h2>End User License Agreement</h2>
            <p>
                ATTENTION: PLEASE READ THE TERMS AND CONDITIONS OF THIS END USER LICENSE AGREEMENT (THIS
                “<strong>EULA</strong>”) CAREFULLY BEFORE DOWNLOADING, INSTALLING OR USING THE LICENSED
                SOFTWARE. INSTALLATION, DOWNLOAD, AND/OR USE OF THE LICENSED SOFTWARE PROVIDED HEREIN IS
                SUBJECT TO YOUR AGREEMENT TO THE TERMS AND CONDITIONS BELOW WHICH CONSTITUTES A LEGAL
                AGREEMENT BETWEEN YOU, THE END USER, AND LSQ FUNDING GROUP, L.C. AND LS DE LLC
                (“LSQ”).&nbsp; BY CLICKING “ACCEPT” YOU INDICATE THAT YOU HAVE READ, UNDERSTAND, AND
                ACCEPT THE TERMS AND CONDITIONS BELOW.&nbsp; IF YOU DO NOT ACCEPT THE TERMS OF THIS
                EULA, YOU SHOULD NOT DOWNLOAD, INSTALL OR USE THE LICENSED SOFTWARE. ALL CAPITALIZED
                TERMS USED BUT UNDEFINED HEREIN SHALL HAVE THE MEANING SET FORTH IN THE AGREEMENT
                BETWEEN THE ENTITY PROVISIONING THE LICENSED SOFTWARE (THE “<strong>LICENSEE</strong>”)
                AND LSQ (THE “<strong>MASTER AGREEMENT</strong>”).
            </p>
            <p>&nbsp;</p>
            <ol>
                <li>
                    <strong>License Grant.</strong>&nbsp;Subject to the terms and conditions set forth
                    in the Master Agreement, LSQ grants Licensee and its authorized End Users a limited,
                    revocable, non-exclusive, non-transferable, non-sublicenseable license during the
                    Term of this EULA to (i) access and use that portion of LSQ’s System as described in
                    the Master Agreement (the “<strong>Licensed Software</strong>”) under the terms of
                    this EULA; and (ii) use the information and other supporting materials we provide to
                    you in connection with the Licensed Software and/or provision of services hereunder
                    (the “<strong>Documentation</strong>”) and make a reasonable number of copies solely
                    as necessary to allow you to use the Licensed Software, for your internal business
                    purposes.&nbsp; The Documentation may not be copied, modified or used in any way not
                    expressly authorized by this license. Ownership, rights and intellectual property in
                    the Licensed Software and Documentation shall remain with us or our licensors.&nbsp;
                    No rights to the Licensed Software are granted except as specifically provided in
                    this Section
                </li>
                <li>
                    <strong>Your Use of the Licensed Software.</strong><br />
                    (a) You are responsible for the development, modification, content, backup and
                    maintenance of any data, documents, forms, templates, information or other materials
                    uploaded by you (or persons accessing the Licensed Software through your facilities
                    or logon information allocated to you) (collectively, “<strong
                        >Your Data and Materials</strong
                    >”) for use with the Licensed Software.(b) We do not vet or approve Your Data and
                    Materials available through the Licensed Software and neither we nor our licensors
                    or service providers accept any liability for Your Data and Materials.(c) You grant
                    to us and our licensors and service providers an irrevocable, perpetual,
                    royalty-free license to reproduce, publish, copy or transmit Your Data and Materials
                    (including the right to aggregate your Data and Materials with other data and
                    distribution thereof to third parties, in a de-identified form) for purposes of
                    supplying the Licensed Software, improving the Licensed Software and similar
                    services, ensuring compliance with the terms applicable to the Licensed Software,
                    verifying the charges and payments applicable to the Licensed Software, and for
                    LSQ’s other internal business purposes.(d) You grant us and our licensors and
                    service providers the irrevocable, perpetual right to compile statistics about your
                    use of the Licensed Software, and to otherwise evaluate and improve the Licensed
                    Software based on your usage and Your Data and Materials, and to make such
                    statistics available to third parties only in a de-identified form.(e) You authorize
                    us to amend or delete any of Your Data and Materials where we become aware (through
                    receipt of a third party notice or otherwise) that Your Data and Materials are, or
                    are alleged to be, in breach of copyright, illegal or not appropriate in our opinion
                    to be accessed by or through the Licensed Software.
                    <p></p>
                    <p>
                        (f) We make no warranty or guarantee that the Licensed Software will be
                        compatible with your existing hardware software or environment.
                    </p>
                    <p>
                        (g) You acknowledge that the Licensed Software may access or be provided via
                        third party software. The licensor of any third party software accessed or
                        provided with the Licensed Software has a proprietary interest in such software,
                        and may impose separate terms and conditions applicable to the use of such third
                        party software, and our third party provider(s) may also impose separate terms
                        and conditions applicable to the procurement of such software.
                    </p>
                    <p>
                        (h) To the fullest extent permitted by law, you access and use Your Data and
                        Materials and the Licensed Software at your own risk. You accept responsibility
                        for all of Your Data and Materials and indemnify us, our licensors and service
                        providers against any liability, costs, losses, claims, or damages (including
                        legal fees and disbursements) incurred or suffered by us in relation to:
                        (1)&nbsp; Your Data and Materials, including but not limited to claims for:
                        infringement of intellectual property rights or other proprietary rights;
                        violation of laws, regulations, or codes; injury or death; breach of privacy or
                        confidence; and (2) Breach of this EULA and enforcing our rights under the EULA.
                    </p>
                </li>
                <li>
                    <strong>License Restrictions.</strong>&nbsp;You may not: (a) copy, reproduce,
                    translate, adapt, modify, reverse engineer, decompile, modify or create derivative
                    works of the Licensed Software; (b) resell, rent, lease, assign or otherwise
                    transfer rights to the Licensed Software; or (c) distribute, provide or otherwise
                    make available the Licensed Software in any form to any person other than as
                    explicitly specified herein.
                </li>
                <li>
                    <strong>License Obligations.</strong>&nbsp;You shall: (a) use the Licensed Software
                    for lawful purposes only; (b) not use the Licensed Software in a way which
                    interferes with or disrupts the hosting facilities or any other user’s use of the
                    Licensed Software; (c) comply with the operational rules (if any), acceptable use
                    policies and technical requirements that we make available to You from time to time;
                    (d) take such actions as are required to ensure that Your access and use of the
                    Licensed Software complies with any laws, statutes, regulations, decrees or orders
                    applicable to Your use of the Licensed Software (including but not limited to laws
                    regarding privacy, export and import); and (e) not take any steps to defeat any host
                    system controls over the download of parts of the site or software that can be
                    downloaded.(a) You acknowledge and agree that the Licensed Software may not be
                    exported or otherwise used in Cuba, Iran, Libya, North Korea, Syria or any other
                    country to which the United States has embargoed goods, or to a national or resident
                    thereof, or to anyone on the United States Treasury Department’s List of Specially
                    Designated Nations, or the United States Commerce Department’s Table of Denial
                    Orders. You further represent and warrant that you are not located in, under the
                    control of, or a national or resident of any such country or on any such list.(b)
                    Licensee shall indemnify LSQ and our licensors and service providers fully against
                    all liabilities, costs and expenses that we may suffer as a result of your or your
                    End User’s breach of this EULA. Licensee shall take such actions as required to
                    ensure that Licensee and End Users’ use of the Licensed Software complies with all
                    applicable laws, statutes, regulations, decrees or orders applicable to Licensee and
                    End Users’ use of the Licensed Software.
                </li>
                <li>
                    <strong>Confidential Information.</strong>&nbsp;You agree to keep confidential and
                    not to use for any purpose other than as necessary to allow you to use the Licensed
                    Software as permitted herein, all information relating to the installation,
                    performance, operation, structure, methods, programming and documentation of the
                    Licensed Software, including the Documentation, unless you have obtained our prior
                    written approval to your proposed use or disclosure.
                </li>
                <li>
                    <strong>Software Version.</strong>&nbsp;We reserve the right to modify or update the
                    Licensed Software as we deem necessary.&nbsp;The Licensed Software may be modified
                    to better meet the needs of our customers.&nbsp;We may, from time to time, offer
                    additional functionality, modules or products, which may, in our discretion, be
                    incorporated into the Licensed Software, or be offered as optional, separately
                    priced services or modules.
                </li>
                <li>
                    <strong>Passwords.</strong>&nbsp;You are fully responsible for the safekeeping of
                    your user identification codes and passwords and for all use of or access to the
                    Licensed Software by anyone using your user identification and passwords.&nbsp;You
                    are solely responsible for any disclosure of the user identification codes or
                    passwords provided to you. We may, by notice in writing, require you to immediately
                    change any of your user identification codes or passwords.&nbsp; You agree to
                    contact us immediately if you suspect any unauthorized use or disclosure of your
                    user identification codes or passwords.&nbsp;We may immediately terminate or suspend
                    user passwords if we (in our discretion) consider this to be necessary or
                    appropriate, including where (without limitation) the Master Agreement (or your
                    involvement in it) ends, or for the reasons described herein.
                </li>
                <li>
                    <strong>Warranty Disclaimer.</strong>&nbsp;THE LICENSED SOFTWARE, WEBSITE AND
                    CONTENT AND/OR ANY RELATED MATERIALS ARE PROVIDED “AS IS” AND WITHOUT WARRANTY OF
                    ANY KIND.&nbsp;WE EXPRESSLY DISCLAIM ALL WARRANTIES IN RELATION TO THE SERVICE,
                    EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF
                    MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE. WE DO NOT WARRANT THAT THE
                    FUNCTIONS CONTAINED IN THE LICENSED SOFTWARE WILL MEET YOUR NEEDS, OR THAT THE
                    OPERATION OF THE LICENSED SOFTWARE WILL BE UNINTERRUPTED OR ERROR-FREE, NOR DO WE
                    MAKE ANY WARRANTIES, EXPRESS OR IMPLIED, WITH RESPECT TO DATA OR DOCUMENTATION
                    ASSOCIATED WITH THE SERVICE, OR FOR THE ACCURACY, CURRENCY OR COMPREHENSIVENESS OF
                    THE SAME. NO OTHER ORAL OR WRITTEN INFORMATION PROVIDED BY US, OR BY ANY THIRD PARTY
                    SHALL CREATE A WARRANTY OR INCREASE THE SCOPE OF ANY WARRANTY.
                </li>
                <li>
                    <strong>Limitation of Liability.</strong>&nbsp;To the maximum extent permitted by
                    applicable law, neither we nor any licensor or service provider shall be liable for
                    any damages whatsoever (including, without limitation, damages for loss of business
                    profits, business interruption, loss of business information, or other pecuniary
                    loss) arising out of the use of or inability to use the Licensed Software.
                </li>
                <li>
                    <strong>Copyright and Trademark.</strong>&nbsp;You acknowledge that the Licensed
                    Software and the Documentation are protected by copyright laws and international
                    copyright treaties, as well as other intellectual property rights and treaties. You
                    will not, during or any time after the termination of this EULA or discontinued use
                    of the Licensed Software, commit or permit any act which infringes those
                    intellectual property rights. You acknowledge that certain marks identified as
                    registered or unregistered trademarks or service marks are the exclusive property of
                    LSQ or its licensors or service providers, and that no right to use such marks is
                    granted pursuant to this EULA. You will not remove, deface or combine with any other
                    mark or symbol, any marks contained in the System or provided by LSQ.
                </li>
                <li>
                    <strong>Term of EULA.</strong>&nbsp;This EULA commences when you receive your login
                    credentials for the Licensed Software and shall continue until the expiration or
                    earlier termination of the Master Agreement with the Licensee. This EULA and your
                    access to the Licensed Software and Documentation shall automatically terminate if
                    you fail to comply with any of the terms and conditions set forth herein.
                </li>
                <li>
                    <strong>EULA Updates</strong>.&nbsp; This EULA may be revised at any time. You
                    should visit LSQ FastTrack® from time to time to review the then-current EULA
                    because it is binding upon you.&nbsp;Your continued use of the Licensed Software
                    constitutes your acceptance of any revised or updated terms of use.
                </li>
                <li>
                    <strong>Governing Law</strong>.&nbsp; This EULA shall be construed, interpreted and
                    governed by the laws of the State of Delaware. The UN Convention on Contracts for
                    the International Sale of Goods is hereby expressly excluded from applicability to
                    this EULA. You submit to the exclusive jurisdiction of the Federal and State courts
                    located in Orange County, Florida.
                </li>
            </ol>
            <p>
                BY DOWNLOADING, INSTALLING, OR USING THE LICENSED SOFTWARE, YOU INDICATE THAT YOU HAVE
                READ, UNDERSTAND, AND ACCEPT THE TERMS AND CONDITIONS ABOVE AND THAT THIS IS THE
                COMPLETE AND EXCLUSIVE STATEMENT OF THE AGREEMENT RELATING TO THE LICENSED SOFTWARE
                BETWEEN US.
            </p>
        `;
    public isAccepting = false;

    public buttonSize$ = this.responsiveService.smBelow.pipe(
        map((isSmall) => (isSmall ? 'block' : 'lg'))
    );

    constructor(
        private _i18n: I18n,
        private _userService: UserService,
        public responsiveService: ResponsiveService,
        private _eulaService: EulaService
    ) {}

    public async acceptEula(): Promise<void> {
        this.isAccepting = true;
        await this._eulaService.acceptEula();
        await this._eulaService.fetchEulaAcceptedDate();

        await this._userService.getAndEmitCurrentUser();
        await this._userService.navigateToHome();
    }
}
